import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { SetAsDefaultCase202 } from "~/api/model";
import { useSetAsDefaultCase } from "~/api/projects/projects";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { getStoredUsername, useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";

const DEV_SET_DEFAULT_SCHEMA = z.object({
  caseName: z.string().optional(),
  industry: z.string().optional(),
  description: z.string().optional(),
});

type SetAsDefaultCaseForm = z.infer<typeof DEV_SET_DEFAULT_SCHEMA>;

const SET_AS_DEFAULT_INITIAL_VALUES: SetAsDefaultCaseForm = {
  caseName: "john-doe",
  industry: "fallback",
  description: "",
};

interface SetAsDefaultCaseDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectId: string;
}

export const SetAsDefaultCaseDialog: FC<SetAsDefaultCaseDialogProps> = ({
  open,
  setOpen,
  projectId,
}) => {
  const { organizationId } = useOrganizationParams();
  const { toast } = useToast();
  const { mutateAsync: setAsDefaultCase, isPending } = useSetAsDefaultCase();
  const [exportResult, setExportResult] = useState<{
    response: SetAsDefaultCase202;
    description: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SetAsDefaultCaseForm>({
    resolver: zodResolver(DEV_SET_DEFAULT_SCHEMA),
    defaultValues: SET_AS_DEFAULT_INITIAL_VALUES,
  });

  const onSubmit = async (data: SetAsDefaultCaseForm) => {
    const username = getStoredUsername();
    if (!username) {
      toast({
        title: "Cannot export",
        description:
          "No stored username found. Sign out and sign in again, then retry.",
        variant: "destructive",
      });
      return;
    }

    trackEvent({
      eventName: "app/cases/set_as_default_case_modal/submit",
    });

    try {
      const trimmedCaseName = data.caseName?.trim() ?? "";
      const trimmedIndustry = data.industry?.trim() ?? "";
      const trimmedDescription = data.description?.trim() ?? "";

      const response = await setAsDefaultCase({
        organizationId,
        projectId,
        data: {
          username,
          ...(trimmedCaseName ? { caseName: trimmedCaseName } : {}),
          ...(trimmedIndustry ? { industry: trimmedIndustry } : {}),
          ...(trimmedDescription ? { description: trimmedDescription } : {}),
        },
      });
      setExportResult({ response, description: trimmedDescription });
    } catch {
      toast({
        title: "Export failed",
        description: "Could not queue default-case export. Please try again.",
        variant: "destructive",
      });
    }
  };

  const onOpenChange = (next: boolean) => {
    if (!next) {
      reset(SET_AS_DEFAULT_INITIAL_VALUES);
      setExportResult(null);
    }
    setOpen(next);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add to demo cases</DialogTitle>
          <DialogDescription className="sr-only">
            Developer-only action. Queues copying this case to default-case
            seeds on the server.
          </DialogDescription>
        </DialogHeader>
        {exportResult ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {exportResult.response.message}
            </p>
            <div className="rounded-md border bg-muted/40 p-3 space-y-1 text-sm">
              <div>
                <span className="font-medium">Path: </span>
                <code className="rounded bg-muted px-1 py-0.5 text-xs">
                  {exportResult.response.seedPathSegment}
                </code>
              </div>
              <div>
                <span className="font-medium">Case name: </span>
                {exportResult.response.caseName}
              </div>
              <div>
                <span className="font-medium">Industry: </span>
                {exportResult.response.industry || "—"}
              </div>
              <div>
                <span className="font-medium">Version: </span>
                {exportResult.response.version}
              </div>
              {exportResult.description && (
                <div>
                  <span className="font-medium">Description: </span>
                  {exportResult.description}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="set-as-default-case-name">
                Demo case name (optional)
              </Label>
              <Input
                id="set-as-default-case-name"
                autoComplete="off"
                disabled={isPending}
                {...register("caseName")}
              />
              <p className="text-xs text-muted-foreground">
                If empty, the field is not sent; the API uses{" "}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">
                  fallback-case-name
                </code>{" "}
                from default-cases/config.json.
              </p>
              {errors.caseName && (
                <p className="text-sm text-destructive">
                  {errors.caseName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="set-as-default-industry">
                Industry (optional)
              </Label>
              <Input
                id="set-as-default-industry"
                autoComplete="off"
                disabled={isPending}
                {...register("industry")}
              />
              <p className="text-xs text-muted-foreground">
                If empty, not sent; server default is typically{" "}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">
                  fallback
                </code>
                .
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="set-as-default-description">
                Description (optional)
              </Label>
              <Textarea
                id="set-as-default-description"
                autoComplete="off"
                rows={3}
                disabled={isPending}
                placeholder="Human-readable note stored in config.json for this version"
                {...register("description")}
              />
              {errors.description && (
                <p className="text-sm text-destructive">
                  {errors.description.message}
                </p>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Queueing…
                  </>
                ) : (
                  "Queue export"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
